import { z } from 'zod';
import { restricted } from './redaction.js';
/**
 * Authorizations, their usage counters, and the payer rules that split a visit
 * between two payers (slice-01 §3.3, spec §6.3.3 and §18.2).
 *
 * ## Status is not in the create or update shape
 *
 * `authorization.status` is **derived on read** — see ADR-019. `EXPIRED` and
 * `EXHAUSTED` are computed from `end_date` and the counters at the moment
 * somebody looks, so there is no request shape that lets a client set them and
 * no way for a stored value to be stale. `PENDING`, `ACTIVE` and `CANCELLED`
 * are decisions a human makes and are settable; the two derived ones are not
 * in `authorizationDecisionSchema` at all, which is the type system carrying
 * the rule.
 *
 * ## The four counters
 *
 * Scheduled, verified, billed and paid, all of them, from the first migration.
 * Only `scheduled` moves in this slice. §6.3.3 is explicit that collapsing
 * them "costs agencies money and is very hard to unwind": the scheduler blocks
 * against scheduled + verified, the biller reconciles against billed, finance
 * reports paid. They are four different questions and one number cannot answer
 * them.
 */
export const authorizationPeriodTypeSchema = z.enum(['TOTAL', 'WEEKLY', 'MONTHLY', 'DAILY']);
export const authorizationStatusSchema = z.enum([
    'PENDING',
    'ACTIVE',
    'EXHAUSTED',
    'EXPIRED',
    'CANCELLED',
]);
/**
 * The statuses a person may set.
 *
 * `EXPIRED` and `EXHAUSTED` are absent deliberately. They are facts about a
 * date and a count, not decisions, and accepting them here would let a caller
 * assert something the data contradicts.
 */
export const authorizationDecisionSchema = z.enum(['PENDING', 'ACTIVE', 'CANCELLED']);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a calendar date as YYYY-MM-DD.');
// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------
/** One `authorization_usage` row — a period and its four counters. */
export const authorizationUsagePeriodSchema = z.object({
    id: z.string().uuid(),
    periodStart: isoDate,
    periodEnd: isoDate,
    scheduledUnits: z.number().int(),
    verifiedUnits: z.number().int(),
    billedUnits: z.number().int(),
    paidUnits: z.number().int(),
});
export const authorizationSchema = z.object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    payerId: z.string().uuid(),
    serviceCodeId: z.string().uuid(),
    authNumber: z.string(),
    authorizedUnits: z.number().int(),
    unitsPerPeriod: z.number().int().nullable(),
    periodType: authorizationPeriodTypeSchema,
    startDate: isoDate,
    endDate: isoDate,
    /**
     * Derived at read time, never read from the column. ADR-019.
     *
     * The column still exists and still holds the human decision — `PENDING`,
     * `ACTIVE`, `CANCELLED` — which is why this is a wider enum than
     * `authorizationDecisionSchema`.
     */
    status: authorizationStatusSchema,
    /**
     * PHI. §6.3.3 lists `diagnosis_codes` on the authorization, and a diagnosis
     * is the single most sensitive field in the intake record — it is what a
     * person would least like a scheduler's shoulder-surfer to read.
     */
    diagnosisCodes: restricted(z.array(z.string()), 'client:read_phi'),
    referringProviderNpi: restricted(z.string().nullable(), 'client:read_phi'),
    alertThresholdPct: z.number().int(),
    /** Consumption against the whole authorization, as a percentage, 0–100+. */
    consumedPct: z.number(),
    /** True once consumption has reached `alertThresholdPct`. */
    alertRaised: z.boolean(),
    remainingUnits: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const authorizationListSchema = z.object({
    authorizations: z.array(authorizationSchema),
});
export const authorizationUsageSchema = z.object({
    authorizationId: z.string().uuid(),
    status: authorizationStatusSchema,
    authorizedUnits: z.number().int(),
    /** Scheduled + verified, which is what capacity is checked against. */
    committedUnits: z.number().int(),
    remainingUnits: z.number().int(),
    consumedPct: z.number(),
    alertThresholdPct: z.number().int(),
    alertRaised: z.boolean(),
    periods: z.array(authorizationUsagePeriodSchema),
});
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
export const createAuthorizationSchema = z
    .object({
    clientId: z.string().uuid(),
    payerId: z.string().uuid(),
    serviceCodeId: z.string().uuid(),
    authNumber: z.string().trim().min(1).max(64),
    authorizedUnits: z.number().int().positive(),
    unitsPerPeriod: z.number().int().positive().nullish(),
    periodType: authorizationPeriodTypeSchema,
    startDate: isoDate,
    endDate: isoDate,
    status: authorizationDecisionSchema.default('PENDING'),
    diagnosisCodes: z.array(z.string().trim().min(1).max(16)).max(24).default([]),
    referringProviderNpi: z
        .string()
        .regex(/^\d{10}$/, 'An NPI is exactly ten digits.')
        .nullish(),
    alertThresholdPct: z.number().int().min(1).max(100).default(80),
})
    .strict()
    .refine((value) => value.endDate >= value.startDate, {
    path: ['endDate'],
    message: 'The end date cannot be before the start date.',
})
    /**
     * A period cap without a period figure is the misconfiguration that mis-caps
     * every visit in the period, so it is refused at the edge rather than
     * defaulted. `checkAuthorizationCapacity` treats a missing figure as "no
     * period limit" — the safe reading for data already stored — but accepting it
     * on the way in would manufacture exactly the data that reading exists to
     * survive.
     */
    .refine((value) => value.periodType === 'TOTAL' || value.unitsPerPeriod != null, {
    path: ['unitsPerPeriod'],
    message: 'A weekly, monthly or daily authorization needs a per-period unit cap.',
});
export const updateAuthorizationSchema = z
    .object({
    authNumber: z.string().trim().min(1).max(64).optional(),
    authorizedUnits: z.number().int().positive().optional(),
    unitsPerPeriod: z.number().int().positive().nullish(),
    endDate: isoDate.optional(),
    status: authorizationDecisionSchema.optional(),
    diagnosisCodes: z.array(z.string().trim().min(1).max(16)).max(24).optional(),
    referringProviderNpi: z
        .string()
        .regex(/^\d{10}$/, 'An NPI is exactly ten digits.')
        .nullish(),
    alertThresholdPct: z.number().int().min(1).max(100).optional(),
})
    .strict();
export const authorizationListQuerySchema = z.object({
    clientId: z.string().uuid().optional(),
    payerId: z.string().uuid().optional(),
    /** Filters on the **derived** status, so `EXPIRED` works without a job. */
    status: authorizationStatusSchema.optional(),
    includeDeleted: z.coerce.boolean().optional(),
});
// ---------------------------------------------------------------------------
// client_payer_rule — §18.2
// ---------------------------------------------------------------------------
export const appliesWhenSchema = z
    .object({
    /** ISO day numbers, 1 = Monday … 7 = Sunday. Absent means every day. */
    daysOfWeek: z.array(z.number().int().min(1).max(7)).max(7).optional(),
    startTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Expected a local time as HH:MM.')
        .optional(),
    endTime: z
        .string()
        .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Expected a local time as HH:MM.')
        .optional(),
})
    .strict();
export const clientPayerRuleSchema = z.object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    payerId: z.string().uuid(),
    serviceCodeId: z.string().uuid(),
    priority: z.number().int(),
    appliesWhen: appliesWhenSchema,
    maxUnitsPerPeriod: z.number().int().nullable(),
    periodType: authorizationPeriodTypeSchema.nullable(),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullable(),
});
export const clientPayerRuleListSchema = z.object({
    /** Ascending by priority — the order §18.2 resolves them in. */
    rules: z.array(clientPayerRuleSchema),
    /**
     * §6.3.2's fall-through, echoed here because a rule list is unreadable
     * without it: the last line of every allocation is "and the rest goes here".
     */
    defaultPayerId: z.string().uuid().nullable(),
});
const payerRuleInputSchema = z
    .object({
    payerId: z.string().uuid(),
    serviceCodeId: z.string().uuid(),
    priority: z.number().int().min(0).max(10_000),
    appliesWhen: appliesWhenSchema.default({}),
    maxUnitsPerPeriod: z.number().int().positive().nullish(),
    periodType: authorizationPeriodTypeSchema.nullish(),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullish(),
})
    .strict()
    .refine((value) => value.effectiveTo == null || value.effectiveTo >= value.effectiveFrom, {
    path: ['effectiveTo'],
    message: 'The end date cannot be before the start date.',
});
/**
 * The whole ordered set, replaced at once.
 *
 * A PUT rather than per-rule POSTs because the rules are a **sequence** and
 * their meaning is positional: adding one at priority 5 changes what the rule
 * at 10 absorbs. Editing them one at a time means the client is briefly in a
 * state nobody designed, and the window is exactly when a visit might be
 * allocated.
 *
 * Duplicate priorities are refused here rather than reported. The domain's
 * `allocateMinutes` reports them because it has to survive whatever is already
 * stored; this is the edge, and the edge can simply say no.
 */
export const putClientPayerRulesSchema = z
    .object({
    rules: z.array(payerRuleInputSchema).max(50),
})
    .strict()
    .refine((value) => new Set(value.rules.map((rule) => rule.priority)).size === value.rules.length, {
    path: ['rules'],
    message: 'Two rules share a priority, so which payer absorbs first is undecided. Give each rule its own priority.',
});
/**
 * Who pays, in the order minutes are absorbed (§7, exit criterion 3).
 *
 * Deliberately an *answer* rather than the inputs to one. A console handed
 * rules, authorizations and usage counters would have to assemble this itself,
 * and then the field app and every report would assemble it again, slightly
 * differently. `covers` is already a sentence for the same reason: a condition
 * object on screen is a translation the reader has to perform.
 */
export const coverageSourceSchema = z.object({
    id: z.string(),
    payerName: z.string(),
    /** "Weekdays before 6pm". Never a condition object. */
    covers: z.string(),
    /** Null when the arrangement has no cap — private pay usually does not. */
    authorizedUnits: z.number().int().nonnegative().nullable(),
    usedUnits: z.number().int().nonnegative(),
    /** So hours can be shown beside units. Payers speak units; coordinators do not. */
    minutesPerUnit: z.number().int().positive(),
    periodLabel: z.string().nullable(),
    /** The `client.default_payer_id` row. Absent entirely when there is none. */
    isFallThrough: z.boolean(),
    status: z.string().nullable(),
});
export const coverageSchema = z.object({ sources: z.array(coverageSourceSchema) });
/**
 * The date the coverage question is about. Defaults to today.
 *
 * Coverage is a window, not a fact — a rule lapses, an authorization expires —
 * so "who pays" is only answerable about a day. Today is the day a coordinator
 * usually means, and next Tuesday is the day they mean when they are booking
 * next Tuesday.
 *
 * The same parameter is on `GET /v1/clients`, and both resolve it through the
 * one definition in the API so the roster and this endpoint cannot disagree
 * about a client on a date.
 */
export const coverageQuerySchema = z.object({
    asOf: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-19 — four digits, two, two.')
        .optional(),
});
//# sourceMappingURL=authorization.js.map